// CreateProductPage.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getCategoryList,
    getCategoryFilterList,
    getSearchedColorList,
    fetchProductDetails
} from '../../../../../utils/api-service/get-services';
import {
    getMethod,
    postMethod,
    putMethod
} from '../../../../../utils/api-service/api-service';
import ColorwaySection from '../../Components/ColorwaySection';
import FilterSection from '../../Components/FilterSection';
import PairWithSection from '../../Components/PairWithSection';
import MultiSelectDropdown from '../../../../Reusables/MultiSelectDropdown/MultiSelectDropdown';
import { getImageKeyFromUrl, uploadToCloudinary, uploadToDigitalOcean } from '../../../../../helpers/upload';
// import './CreateProductPage.css';

const BasicDetails = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    // Form state
    const [productData, setProductData] = useState({
        name: '',
        code: '',
        category: '',
        collectionType: 'CRAFTED',
        skuType: "",
        collectionPartner: '',
        availableAcrossCategories: [],
        description: '',
        details: '',
        specifications: '',
        technicalSheetUrl: '',
        filters: [],
        colorways: [],
        pairedProducts: [],
        moodShotImage: null,
        moodShotImageExist: false
    });

    // UI state
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [collectionPartners, setCollectionPartners] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([
        {
            name: "Curtain",
            value: "CURTAIN"
        },
        {
            name: "Blinds",
            value: "BLIND"
        },
        {
            name: "Cushion",
            value: "CUSHION"
        },
        {
            name: "Bedding",
            value: "BEDDING"
        },
        {
            name: "Upholstery",
            value: "UPHOLSTERY"
        },
        {
            name: "Panel",
            value: "PANEL"
        },
    ]);
    const [productTypes] = useState([
        {
            name: "Edition Main",
            value: "Edition_Main"
        },
        {
            name: "Edition Sheer",
            value: "Edition_Sheer"
        },
        {
            name: "Edition Main Printed",
            value: "Edition_Main_Printed"
        },
        {
            name: "Edition Sheer Printed",
            value: "Edition_Sheer_Printed"
        },
        {
            name: "Partner Main",
            value: "Partner_Main"
        },
        {
            name: "Partner Sheer",
            value: "Partner_Sheer"
        },
        {
            name: "Partner Edition Main",
            value: "Partner_Edition_Main"
        },
        {
            name: "Partner Edition Sheer",
            value: "Partner_Edition_Sheer"
        },
    ])
    const [filterGroups, setFilterGroups] = useState([]);
    const [technicalFile, setTechnicalFile] = useState(null);

    // Fetch initial data
    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (isEditMode && categories.length > 0) {
            fetchProduct();
        }
    }, [categories, isEditMode]);

    const fetchProduct = async () => {
        const response = await fetchProductDetails(id);
        if (response && response.success !== false) {
            setProductData({
                _id: response._id,
                name: response.name || "",
                code: response.code || "",
                category: response.category?._id || "",
                collectionType: response.collectionType || "CRAFTED",
                skuType: response?.skuType || "",
                collectionPartner: response.collectionPartner?._id || null,
                availableAcrossCategories: response.availableAcrossCategories || [],
                description: response.description || "",
                details: response.details || "",
                specifications: response.specifications || "",
                technicalSheetUrl: response.technicalSheetUrl || "",
                filters: response.filters?.map(f => f._id) || [],
                colorways: response.colorways || [],
                pairedProducts: response.pairedProducts || [],
                moodShotImage: response.moodShotImage || null,
                moodShotImageExist: Boolean(response.moodShotImage),
            });

            if (response.category?._id && categories) {
                handleCategoryChange(response.category._id, { preserveFilters: true });
            }
        } else {
            toast.error(response.message || "Failed to fetch product details");
        }
    };

    const fetchInitialData = async () => {
        try {
            // Fetch categories
            const categoriesRes = await getCategoryList();
            if (categoriesRes.success) {
                const parentCategories = categoriesRes?.tree.filter(cat => cat.level === 0);
                setCategories(parentCategories);
            }

            // Fetch collection partners
            const partnersRes = await getMethod('/collection-partner/list');
            if (partnersRes.success) {
                setCollectionPartners(partnersRes.partners);
            }

        } catch (error) {
            toast.error('Failed to load initial data');
        }
    };

    // Handle category selection
    const handleCategoryChange = async (categoryId, { preserveFilters = false } = {}) => {
        const rootCategory = findRootCategory(categories, categoryId);

        setProductData(prev => ({
            ...prev,
            category: categoryId,
            filters: preserveFilters ? prev.filters : []
        }));

        // Pass category data to parent
        if (rootCategory) {
            props.onCategoryChange(rootCategory);
        }

        if (rootCategory?._id) {
            await fetchFiltersForCategory(rootCategory._id);
        }
    };

    const findRootCategory = (categories, categoryId) => {
        for (const category of categories) {
            if (category._id === categoryId) {
                return category; // selected is root
            }

            if (category.children?.length) {
                if (category.children.some(child => child._id === categoryId)) {
                    return category; // parent is root
                }

                const found = findRootCategory(category.children, categoryId);
                if (found) return found;
            }
        }
        return null;
    };

    const uploadProductImages = async () => {

        /* ---------- MOOD SHOT ---------- */

        let moodShot = productData.moodShotImage;

        if (moodShot?.file) {
            const res = await uploadToDigitalOcean(moodShot.file, {
                module: "product",
            });

            moodShot = {
                key: res.key,
                url: res.secure_url,
                alt: "Mood shot"
            };
        }

        /* ---------- COLORWAYS ---------- */
        console.log("Upploading images----------")

        const uploadedColorways = await Promise.all(
            productData.colorways.map(async (cw) => {

                const uploadedImages = await Promise.all(
                    cw.images.map(async (img) => {
                        console.log("img: ", img)
                        if (!img.isTemporary || !img.file) {
                            // Ensure we have the key for existing images
                            if (img.url && !img.key) {
                                const key = getImageKeyFromUrl(img.url);
                                return {
                                    ...img,
                                    key: key || img.key,
                                    isTemporary: false
                                };
                            }
                            return img;
                        }

                        const res = await uploadToDigitalOcean(img.file, {
                            module: "product",
                            color: cw.color
                        });

                        return {
                            key: res.key,
                            isTemporary: false,
                            alt: `${productData.name}-${cw.color}`
                        };
                    })
                );
                console.log("Uploaded Images: ", uploadedImages)

                return {
                    ...cw,
                    images: uploadedImages
                };
            })
        );

        console.log("Uploaded colorways: ", uploadedColorways);

        return {
            moodShotImage: moodShot,
            colorways: uploadedColorways
        };
    };

    // Fetch filters for category
    const fetchFiltersForCategory = async (categoryId) => {
        try {
            const filtersRes = await getCategoryFilterList(categoryId);
            if (filtersRes.success) {
                setFilterGroups(filtersRes.filterGroups);
            }
        } catch (error) {
            toast.error('Failed to load filters');
        }
    };

    // Handle filter selection
    const handleFilterChange = (filterId, isSelected) => {
        setProductData(prev => {
            let updatedFilters = [...prev.filters];
            if (isSelected) {
                if (!updatedFilters.includes(filterId)) {
                    updatedFilters.push(filterId);
                }
            } else {
                updatedFilters = updatedFilters.filter(id => id !== filterId);
            }
            return { ...prev, filters: updatedFilters };
        });
    };

    // Handle colorway updates
    const handleColorwaysUpdate = (updatedColorways) => {
        setProductData(prev => ({
            ...prev,
            colorways: updatedColorways
        }));
    };

    // Handle paired products update
    const handlePairedProductsUpdate = (updatedPairedProducts) => {
        setProductData(prev => ({
            ...prev,
            pairedProducts: updatedPairedProducts
        }));
    };

    // Handle technical sheet upload
    const handleTechnicalSheetUpload = async (file) => {
        setTechnicalFile(file);
        // TODO: Upload to Cloudinary/S3 and get URL
        // const uploadRes = await uploadFile(file);
        // setProductData(prev => ({
        //     ...prev,
        //     technicalSheetUrl: uploadRes.url
        // }));
    };

    // Handle mood shot upload
    const handleMoodShotUpload = (file) => {
        if (!file) return;

        const preview = URL.createObjectURL(file);

        setProductData(prev => ({
            ...prev,
            moodShotImage: {
                url: preview,
                file
            },
            moodShotImageExist: true
        }));
    };

    // Validate form
    const validateForm = () => {
        const errors = [];

        if (!productData.name.trim()) errors.push('Product name is required');
        if (!productData.code.trim()) errors.push('Product code is required');
        if (!productData.category) errors.push('Category is required');
        // if (productData.collectionType === 'CURATED' && !productData.collectionPartner) {
        //     errors.push('Collection partner is required for CURATED type');
        // }
        if (!productData.description.trim()) errors.push('Description is required');
        if (!productData.details.trim()) errors.push('Details are required');
        if (!productData.specifications.trim()) errors.push('Specifications are required');
        if (productData.colorways.length === 0) {
            errors.push('At least one colorway is required');
        }
        // if (productData.filters.length === 0) {
        //     errors.push('At least one filter is required');
        // }

        return errors;
    };

    // Save as draft
    const handleSaveDraft = async () => {
        setLoading(true);

        try {

            const uploads = await uploadProductImages();

            const productId = productData._id;

            const url = productId
                ? `/products/${productId}/basic`
                : `/products`;

            const method = productId ? putMethod : postMethod;

            const response = await method(
                url,
                {
                    ...productData,
                    ...uploads,
                    category: productData.category
                },
                { action: "save" }
            );

            if (response.success) {
                toast.success("Product saved as draft");

                if (!productId) {
                    setProductData(prev => ({
                        ...prev,
                        _id: response.product._id
                    }));
                }
            } else {
                toast.error(response.message || "Failed to save draft");
            }
        } catch (err) {
            toast.error("Error saving draft");
        } finally {
            setLoading(false);
        }
    };


    // Save and complete
    const handleSaveComplete = async () => {
        const errors = validateForm();
        if (errors.length > 0) {
            toast.error(errors[0]);
            return;
        }

        setLoading(true);

        try {
            const uploads = await uploadProductImages();
            const productId = productData._id;

            const url = productId
                ? `/products/${productId}/basic?action=complete`
                : `/products`;

            const method = productId ? putMethod : postMethod;

            const response = await method(
                url,
                {
                    ...productData,
                    ...uploads,
                    category: productData.category
                }
            );

            if (response.success) {
                toast.success("Basic details completed");

                // Pass product ID to parent for PricingDetails
                if (!productId && response.product) {
                    props.onProductCreated(response.product._id);
                }

                navigate(`/product/${response.product._id}`);
            } else {
                toast.error(response.message);
                if (response.missingRequirements) {
                    toast.error(response.missingRequirements.join(", "));
                }
            }
        } catch (err) {
            toast.error("Error completing product");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid py-4">
            <div style={{ border: "none" }} className="card border-none">
                <div className="card-body">
                    {/* ========== SECTION 1: CATEGORY & BASIC INFO ========== */}
                    <div className="mb-5">
                        <h5 className="border-bottom pb-2 mb-3">
                            <i className="bi bi-tags me-2"></i>
                            Category & Basic Information
                        </h5>

                        <div className="row">

                            <div className="col-md-6 mb-3">
                                <label className="form-label">Main Category *</label>

                                <select
                                    className="form-select"
                                    value={productData.skuType}
                                    onChange={(e) => setProductData(prev => ({ ...prev, skuType: e.target.value }))}
                                >
                                    <option value="">Select SKU Type</option>

                                    {productTypes.map((pt) => (
                                        <option key={pt.value} value={pt.value}>
                                            {pt.name}
                                        </option>
                                    ))}
                                </select>
                            </div>


                            {/* Main Category */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Main Category *</label>

                                <select
                                    className="form-select"
                                    value={productData.category}
                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                >
                                    <option value="">Select Category</option>

                                    {categories.map((cat) => (
                                        cat.children && cat.children.length ? (
                                            <optgroup key={cat._id} label={cat.name}>
                                                {cat.children.map((child) => (
                                                    <option key={child._id} value={child._id}>
                                                        {child.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ) : (
                                            <option key={cat._id} value={cat._id}>
                                                {cat.name}
                                            </option>
                                        )
                                    ))}
                                </select>
                            </div>

                            {/* Product Code */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Product Code *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={productData.code}
                                    onChange={(e) => setProductData(prev => ({ ...prev, code: e.target.value }))}
                                    placeholder="e.g., CUR-001"
                                />
                            </div>


                            {/* Collection Type */}
                            <div className="col-md-6 mb-3">
                                <label htmlFor='collectionType' className="form-label">Collection Type</label>
                                <div className="d-flex flex-column">
                                    <div className="form-check my-2">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="collectionType"
                                            id="crafted"
                                            value="CRAFTED"
                                            checked={productData.collectionType === 'CRAFTED'}
                                            onChange={(e) => setProductData(prev => ({ ...prev, collectionType: e.target.value }))}
                                        />
                                        <label className="form-check-label" htmlFor="crafted">
                                            Crafted
                                        </label>
                                    </div>
                                    <div className="form-check d-flex row flex-row justify-content-start">
                                        <div className='col-md-3 m-0'>
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="collectionType"
                                                id="curated"
                                                value="CURATED"
                                                checked={productData.collectionType === 'CURATED'}
                                                onChange={(e) => setProductData(prev => ({ ...prev, collectionType: e.target.value }))}
                                            />
                                            <label className="form-check-label" htmlFor="curated">
                                                Curated
                                            </label>
                                        </div>
                                        {/* Collection Partner (only for CURATED) */}
                                        {productData.collectionType === 'CURATED' && (
                                            <div className="col-md-6 px-0 m-0">
                                                <select
                                                    className="form-select"
                                                    value={productData.collectionPartner}
                                                    onChange={(e) => setProductData(prev => ({ ...prev, collectionPartner: e.target.value }))}
                                                >
                                                    <option value="">Select Partner</option>
                                                    {collectionPartners.map(partner => (
                                                        <option key={partner._id} value={partner._id}>
                                                            {partner.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Available Across Categories */}
                            {/* <div className="col-md-12 mb-3">
                                <label className="form-label">Available Across Categories</label>
                                <select
                                    className="form-select"
                                    multiple
                                    value={productData.availableAcrossCategories}
                                    onChange={(e) => {
                                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                                        setProductData(prev => ({ ...prev, availableAcrossCategories: selected }));
                                    }}
                                    size="3"
                                >
                                    {availableCategories.map(cat => (
                                        <option key={cat._id} value={cat._id}>
                                            {cat.label}
                                        </option>
                                    ))}
                                </select>
                                <small className="text-muted">Hold Ctrl/Cmd to select multiple categories</small>
                            </div> */}
                            <div className="col-md-6 mb-3">
                                <MultiSelectDropdown
                                    label="Available Across Categories"
                                    options={availableCategories.map(cat => ({
                                        value: cat.value,
                                        label: cat.name,
                                        name: cat.name,
                                        level: cat.level || cat.indent || 0
                                    }))}
                                    selectedValues={productData.availableAcrossCategories}
                                    onChange={(selected) => setProductData(prev => ({
                                        ...prev,
                                        availableAcrossCategories: selected
                                    }))}
                                    placeholder="Click to select categories..."
                                    enableSearch={true}
                                    isCategoryTree={true}
                                />
                                <small className="text-muted mt-2 d-block">
                                    Select categories where this product should be available alongside its main category
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* ========== SECTION 2: PRODUCT DETAILS ========== */}
                    <div className="mb-5">
                        <h5 className="border-bottom pb-2 mb-3">
                            <i className="bi bi-card-text me-2"></i>
                            Product Details
                        </h5>

                        <div className="row">
                            {/* Product Name */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Product Name *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={productData.name}
                                    onChange={(e) => setProductData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g., Elegant Linen Curtain"
                                />
                            </div>
                            {/* Description */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Description *</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={productData.description}
                                    onChange={(e) => setProductData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Enter product description..."
                                />
                            </div>

                            {/* Details */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Product Details *</label>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    value={productData.details}
                                    onChange={(e) => setProductData(prev => ({ ...prev, details: e.target.value }))}
                                    placeholder="Enter detailed product information..."
                                />
                            </div>

                            {/* Specifications */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Specifications *</label>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    value={productData.specifications}
                                    onChange={(e) => setProductData(prev => ({ ...prev, specifications: e.target.value }))}
                                    placeholder="Enter technical specifications..."
                                />
                            </div>

                            {/* Technical Sheet Upload */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Technical Sheet (PDF)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => handleTechnicalSheetUpload(e.target.files[0])}
                                />
                                {productData.technicalSheetUrl && (
                                    <div className="mt-2">
                                        <a
                                            href={productData.technicalSheetUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-primary"
                                        >
                                            View Uploaded Sheet
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Mood Shot Upload */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Mood Shot Image</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept="image/*"
                                    onChange={(e) => handleMoodShotUpload(e.target.files[0])}
                                />
                                {productData.moodShotImage?.url && (
                                    <div className="mt-2">
                                        <img
                                            src={productData.moodShotImage.url}
                                            alt="Mood shot"
                                            style={{ maxWidth: '100px', maxHeight: '100px' }}
                                            className="img-thumbnail"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ========== SECTION 3: FILTERS ========== */}
                    <div className="mb-5">
                        <FilterSection
                            filterGroups={filterGroups}
                            selectedFilters={productData.filters}
                            onFilterChange={handleFilterChange}
                        />
                    </div>

                    {/* ========== SECTION 4: COLORWAYS ========== */}
                    <div className="mb-5">
                        <ColorwaySection
                            colorways={productData.colorways}
                            onColorwaysUpdate={handleColorwaysUpdate}
                        />
                    </div>

                    {/* ========== SECTION 5: PAIR WITH PRODUCTS ========== */}
                    <div className="mb-5">
                        <PairWithSection
                            pairedProducts={productData.pairedProducts}
                            onPairedProductsUpdate={handlePairedProductsUpdate}
                        />
                    </div>

                    {/* ========== ACTION BUTTONS ========== */}
                    <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/products')}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <div className="d-flex gap-3">
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleSaveDraft}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save as Draft'}
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={handleSaveComplete}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : 'Save & Complete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicDetails;